import { domainLabel, domainSafetyNote, headlineAttribute, rankLabel } from "../../lib/manifest";
import { DateStamp } from "../DateStamp";
import { PhotoMount } from "../PhotoMount";
import { RarityBadge } from "../RarityBadge";
import { SafetyStrip } from "../SafetyStrip";
import type { ScrapbookTemplateProps } from "../ScrapbookPage";

/**
 * The pressed-specimen sheet, for flora.
 *
 * A real herbarium sheet: one large centred print on a big expanse of card,
 * the binomial in italic Fraunces, and one headline fact underneath. Nothing
 * else competes with the specimen for attention.
 */
export function HerbariumSheet({
  entry,
  manifest,
  revealing = false,
  onImageError,
}: ScrapbookTemplateProps) {
  const domain = domainLabel(manifest, entry.domain);
  const rank = rankLabel(manifest, entry.domain, entry.rank);
  const headline = headlineAttribute(manifest, entry.domain, entry.attributes);

  return (
    <article className="sheet grain-strong relative px-4 pt-8 pb-10 sm:px-10 sm:pt-12 sm:pb-14">
      {/* The engraved double rule an institution prints round the sheet. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-3 border border-rule sm:inset-5"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-4 border border-rule opacity-50 sm:inset-6"
      />

      <header className="relative mb-6 text-center sm:mb-10">
        <p className="field-label text-ink-soft">
          {[domain, rank].filter(Boolean).join(" · ") || "Pressed specimen"}
        </p>
      </header>

      <div className="relative mx-auto max-w-xl">
        <PhotoMount
          seedId={entry.id}
          src={entry.imageUrl ?? entry.thumbUrl}
          alt={entry.displayTitle}
          aspect="aspect-3/4"
          fit="cover"
          revealing={revealing}
          eager
          {...(onImageError ? { onImageError } : {})}
        />
      </div>

      <div className={revealing ? "animate-settle" : undefined}>
        <div className="relative mt-10 text-center">
          <h1 className="display-2 text-3xl leading-[1.1] text-ink sm:text-[2.75rem]">
            {entry.displayTitle}
          </h1>
          {entry.scientificName ? (
            <p className="mt-2 display-binomial text-xl italic text-ink-soft sm:text-2xl">
              {entry.scientificName}
            </p>
          ) : null}
          {headline ? (
            <p className="mt-3 font-label text-sm text-ink-soft">
              {headline.label}: <span className="text-ink">{headline.display}</span>
            </p>
          ) : null}
          <div className="mt-4 flex items-center justify-center gap-3">
            <RarityBadge rarity={entry.rarity} />
            <DateStamp date={entry.foundAt} seedId={entry.id} size="sm" />
          </div>
        </div>

        {entry.safetyNote ? (
          <SafetyStrip
            note={entry.safetyNote}
            domainNote={domainSafetyNote(manifest, entry.domain)}
            className="mx-auto mt-6 max-w-prose"
          />
        ) : null}
      </div>
    </article>
  );
}
